/**
 * Zodiac Chess AI Battle System
 *
 * This module provides a complete AI battle system with multiple AI implementations:
 * - Dion's AI: 29-priority decision engine with MCTS fallback
 * - Tactician: Deep minimax with alpha-beta pruning
 * - Explorer: Pure Monte Carlo Tree Search
 * - Zenith: Hybrid combining all approaches
 *
 * Features:
 * - Player vs AI
 * - AI vs AI battles
 * - Move analysis and reporting
 * - Configurable timing constraints (1s min, 10s max)
 */

// Core interfaces and types
export type { IAIPlayer, AIMoveResult, AIStats, BattleConfig } from "./Core/IAIPlayer";
export { AIPlayerType } from "./Core/IAIPlayer";
export { TimeManager } from "./Core/TimeManager";
export type { PlayerSelection, BattleMove, BattleState } from "./Core/AIOrchestrator";
export { AIOrchestrator } from "./Core/AIOrchestrator";

// AI Variants
export { AIVariant, AI_VARIANT_CONFIG, getBaseAIType, getTimeLimit, getDisplayName, getVariantsForBaseType, getAIGridLayout, getRowLabels, getColumnLabels } from "./Core/AIVariants";
export type { AIVariantInfo } from "./Core/AIVariants";

// AI Implementations
export { DionsAI } from "./DionsAI";
export { MinimaxAI } from "./MinimaxAI";
export { MonteCarloAI } from "./MonteCarloAI";
export { UltimateAI } from "./UltimateAI";
export { CreatureAI } from "./CreatureAI";
export { AstraAI } from "./AstraAI";

// Analysis tools
export * from "./Analysis/types";
export { AstraBlindAI } from "./AstraBlindAI";

// Factory function for easy AI creation
import { ZodiacEngine } from "../zodiacEngine";
import { AIOrchestrator } from "./Core/AIOrchestrator";

/**
 * Create a new AI Orchestrator instance
 */
export function createAIOrchestrator(gameEngine: ZodiacEngine): AIOrchestrator {
  return new AIOrchestrator(gameEngine);
}
